/* eslint-disable */
/* eslint-disable @typescript-eslint/camelcase */
import chroma from 'chroma-js';

export const CalendarItemsColor = {
    available: '#7bcf93',
    unavailable: '#e47064',
    job: '#646c70'
}

const color = {
    aquamarine: {
        aquamarine_20: '#B2DFDB',
        aquamarine_70: '#00897B'
    },
    blue: {
        blue_40: '#4FC3F7',
        blue_70: '#1e88E5',
        royal_blue_10: '#e5eefa',
        royal_blue_70: '#2c69c8'
    },
    crimson: {
        crimson_80: '#c12c46',
        crimson_90: '#b4253f'
    },
    dodger_blue: {
        dodger_blue_20: '#BBDEFB',
        dodger_blue_70: '#1E88E5'
    },
    gold: {
        gold_10: '#fff8e1',
        gold_20: '#ffecb3',
        gold_60: '#ffc107',
        gold_70: '#ffb300',
        gold_90: '#ff8f00'
    },
    green: {
        green_10: '#e8f5e5',
        green_20: '#C8E6C9',
        green_30: '#a5d6a7',
        green_60: '#4caf50',
        green_70: '#43A047',
        green_80: '#388e3c'
    },
    grey: {
        grey_0: '#f6f7f8',
        grey_10_hover: '#dee3e7',
        grey_10: '#eceff1',
        grey_100: '#263238',
        grey_20_hover: '#dee3e7',
        grey_20: '#cfd8dc',
        grey_30: '#b0bec5',
        grey_40: '#90a4ae',
        grey_50: '#78909c',
        grey_60: '#607d8b',
        grey_70: '#546e7a',
        grey_80: '#455a64',
        grey_90: '#37474f'
    },
    indigo: {
        indigo_80: '#303f9f'
    },
    orange: {
        orange_70: '#FB8C00'
    },
    red: {
        red_10: '#ffebee',
        red_20: '#ffcdd2',
        red_30: '#ef9a9a',
        red_40: '#e57373',
        red_50: '#ef5350',
        red_60: '#f44336',
        red_70: '#e53935',
        red_80: '#d32f2f',
        red_90: '#c62828'
    },
    rubin: '#D01E53',
    tomato: {
        tomato_20: '#FFCCBC',
        tomato_70: '#F4511E'
    },
    violet: {
        violet_50: '#7e57c2',
        violet_80: '#512da8'
    },
    turquoise: {
        turquoise_10: '#e0f4f5',
        turquoise_20: '#b2e4e4',
        turquoise_30: '#80d2d3',
        turquoise_40: '#4dc0c1',
        turquoise_50: '#26b3b3',
        turquoise_60: '#00a5a6',
        turquoise_70: '#009797',
        turquoise_80: '#008583',
        turquoise_90: '#007370'
    },
    white: '#ffffff'
} as const;

const semanticColorNext = {
    primary: {
        light: "#455A64",
        main: "#37474F",
        contrastText: "#FFFFFF",
        dark: "#324148",
        active: "#9BA2A6"
    },
    text: {
        primary: "#37474F",
        tertiary: "#455A64",
    },
    action: {
        active: "#607D8B",
        selected: '#ECEFF1',
        hover: "#F6F7F8",
        disabled: '#90A4AE'
    },
    inputBorder: '#CFD8DC',
    error: {
        main: "#E53935"
    }
} as const;

const nextPalette = {
    background: {
        navigation: '#FAFAFA',
        disabled: '#FFFFFF1F'
    },
    secondary: {
        main: '#D01C53',
        light: '#E43A6D'
    },
    primary: {
        light: '#455A64',
        main: '#37474F',
        contrastText: '#FFFFFF',
        dark: '#324148'
    },
    warning: {
        main: '#F7CD3B',
        dark: '#F5C414'
    },
    error: {
        main: '#E53935',
        light: '#EF5350'
    },
    action: {
        active: '#607D8B',
        selected: '#ECEFF1',
        hover: '#F6F7F8',
        disabled: '#90A4AE',
        disabledBackground: '#ECEFF1'
    },
    success: {
        main: '#7BCF93',
        light: '#78CE91',
        dark: '#5AC478'
    },
    text: {
        primary: '#37474F',
        secondary: '#607D8B',
        tertiary: '#455A64',
        disabled: '#90A4AE',
        darkSecondary: '#FFFFFFB3',
        dark: '#324148'
    },
    divider: '#ECEFF1',
    inputBorder: '#CFD8DC',
    innerBorder: '#37474f14',
    outlinedBorder: '#CFD8DC',
    contrastBorder: '#E7E9EA'
} as const;

const semanticColor = {
    border: color.grey.grey_20,
    borderDisabled: color.grey.grey_10_hover,
    borderHighContrast: color.grey.grey_90,
    borderMediumContrast: color.grey.grey_50,
    disabled: color.grey.grey_10_hover,
    error: color.red.red_70,
    focus: color.turquoise.turquoise_20,
    highlightPrimary: color.turquoise.turquoise_10,
    highlightSecondary: color.grey.grey_10,
    hover_row: color.grey.grey_10_hover,
    hover_support_01: color.red.red_90,
    hover_support_02: color.green.green_80,
    hover_support_03: color.gold.gold_60,
    hover_ui: color.grey.grey_20_hover,
    hoverPrimary: nextPalette.primary.dark,
    hoverSecondary: '#91133A',
    holiday: color.red.red_90,
    icon_01: color.grey.grey_80,
    icon_02: color.grey.grey_60,
    overlay_01: chroma(color.grey.grey_80).alpha(0.8).css(),
    pageBackground: color.white,
    primary: nextPalette.primary.main,
    primaryOld: color.turquoise.turquoise_50,
    secondary: nextPalette.secondary.main,
    selected: color.turquoise.turquoise_50,
    selectedSemi: color.turquoise.turquoise_30,
    success: color.green.green_60,
    support_01: color.red.red_70,
    support_02: color.green.green_60,
    support_03: color.gold.gold_60,
    surfacePrimary: color.turquoise.turquoise_10,
    tertiaryContainerBackground: color.grey.grey_20,
    textDisabled: color.grey.grey_40,
    textPlaceholder: color.grey.grey_40,
    textPrimary: nextPalette.text.primary,
    textSecondary: nextPalette.text.secondary,
    textTertiary: nextPalette.text.tertiary,
    uiBackground: color.grey.grey_10,
    ui_01: color.grey.grey_0,
    next: semanticColorNext,
    divider: nextPalette.divider
} as const;

const size = {
    size_2: '2px',
    size_5: '4px',
    size_10: '8px',
    size_15: '12px',
    size_20: '16px',
    size_25: '20px',
    size_30: '24px',
    size_40: '32px',
    size_50: '40px',
    size_60: '48px',
    size_70: '56px',
    size_80: '64px',
    size_90: '72px',
    size_100: '80px',
    size_110: '128px',
    size_120: '256px',
    size_125: '320px',
    size_130: '480px'
} as const;

const radius = {
    radius_2: size.size_2,
    radius_5: size.size_5,
    radius_10: size.size_10,
    radius_20: size.size_20,
    radius_40: size.size_40
} as const;

const shadow = {
    shadow_i0: `0 0 0 2px ${ semanticColor.focus }`,
    shadow_i0_red: `0 0 0 2px ${ color.red.red_20 }`,
    shadow_i1: `inset 0 1px 0 0 ${ color.grey.grey_10 }`,
    shadow_i2: `inset 0 1px 0 0 ${ color.grey.grey_10_hover }`,
    shadow_i3: `inset -1px 1px 0 0 ${ color.grey.grey_10_hover }`,
    shadow_i4: `inset 1px -1px 0 0 ${ color.grey.grey_10_hover }`,
    shadow_i1_strong: `inset 0 1px 0 0 ${ color.grey.grey_30 }`,
    shadow_1: `0 1px 3px 0 rgba(73, 91, 119, 0.08), 0 2px 1px 0 rgba(73, 91, 119, 0.04), 0 1px 1px 0 rgba(73, 91, 119, 0.08);`,
    shadow_2: `0 1px 5px 0 rgba(73, 91, 119, 0.16), 0 3px 1px 0 rgba(73, 91, 119, 0.08), 0 2px 2px 0 rgba(73, 91, 119, 0.1);`,
    shadow_3: `0 1px 8px 0 rgba(73, 91, 119, 0.2), 0 3px 3px 0 rgba(73, 91, 119, 0.12), 0 3px 4px 0 rgba(73, 91, 119, 0.14);`,
    shadow_4: `0 2px 4px 0 rgba(73, 91, 119, 0.2), 0 1px 10px 0 rgba(73, 91, 119, 0.12), 0 4px 5px 0 rgba(73, 91, 119, 0.14);`,
    shadow_5: `0 3px 5px 0 rgba(73, 91, 119, 0.2), 0 1px 18px 0 rgba(73, 91, 119, 0.12), 0 6px 10px 0 rgba(73, 91, 119, 0.14);`,
    shadow_6: `0 5px 5px 0 rgba(73, 91, 119, 0.16), 0 3px 14px 0 rgba(73, 91, 119, 0.08), 0 8px 10px 0 rgba(73, 91, 119, 0.1);`,
    shadow_7: `0 7px 8px 0 rgba(73, 91, 119, 0.2), 0 5px 22px 0 rgba(73, 91, 119, 0.12), 0 12px 17px 0 rgba(73, 91, 119, 0.14);`,
    shadow_8: `0 8px 10px 0 rgba(73, 91, 119, 0.2), 0 6px 30px 0 rgba(73, 91, 119, 0.12), 0 16px 24px 0 rgba(73, 91, 119, 0.14);`,
    shadow_9: `0 11px 15px 0 rgba(73, 91, 119, 0.2), 0 9px 46px 0 rgba(73, 91, 119, 0.12), 0 24px 38px 0 rgba(73, 91, 119, 0.14);`
} as const;

const elevation = {
    elevate_10: 'box-shadow: 0px 1px 1px rgba(73, 91, 119, 0.08), 0px 2px 1px rgba(73, 91, 119, 0.04), 0px 1px 3px rgba(73, 91, 119, 0.08);'
} as const;

const breakpoints = {
    mediaQueries: {
        isDesktop: '(min-width: 1200px)',
        isSmallDesktop: '(min-width: 1025px) and (max-width: 1200px)',
        isTablet: '(min-width: 768px) and (max-width: 1024px)',
        isMobile: '(max-width: 767px)'
    },
    tablet: '768px',
    desktop: '1025px'
} as const;

const type = {
    boldFont: 'bold',
    semiBoldFont: 'semiBold',
    lightFont: 'light',
    mediumFont: 'medium',
    monoRegularFont: 'monoRegular',
    regularFont: 'Roboto',
    titleFont: 'Poppins'
} as const;

export { breakpoints, color, elevation, radius, semanticColor, shadow, size, type };
